import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Coupon, COUPON_DISCOUNT_TYPES, CouponUpdateRequest } from '../../types/coupon';
import { updateCoupon } from '../../api/coupons';
import { formatDateToISO } from '../../utils/dateUtils';
import CouponImmediateStartConfirmDialog from './CouponImmediateStartConfirmDialog';

interface CouponEditDialogProps {
  open: boolean;
  onClose: () => void;
  coupon: Coupon;
  onSuccess: () => void;
}

interface FormErrors {
  [key: string]: string;
}

const CouponEditDialog: React.FC<CouponEditDialogProps> = ({
  open,
  onClose,
  coupon,
  onSuccess,
}) => {
  const [ formData, setFormData ] = useState<CouponUpdateRequest>({
    code: '',
    name: '',
    discount_type: 'FIXED',
    discount_value: 0,
    start_date: '',
    end_date: '',
    usage_limit: null,
    remarks: '',
    disabled: false,
  });
  const [ fieldErrors, setFieldErrors ] = useState<FormErrors>({});
  const [ errors, setErrors ] = useState<string[]>([]);
  const [ apiErrors, setApiErrors ] = useState<string[]>([]);
  const [ loading, setLoading ] = useState(false);
  const [ hasChanges, setHasChanges ] = useState(false);
  const [ initialFormData, setInitialFormData ] = useState<CouponUpdateRequest | null>(null);
  const [ showImmediateStartConfirm, setShowImmediateStartConfirm ] = useState(false);
  // クーポンがアクティブかどうかを判定（開始日が今日以前）
  const isActive = React.useMemo(() => {
    if (!coupon.start_date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(coupon.start_date);
    startDate.setHours(0, 0, 0, 0);
    return startDate <= today;
  }, [ coupon.start_date ]);
  // クーポンが未来のものかどうかを判定（開始日が明日以降）
  const isFuture = React.useMemo(() => {
    if (!coupon.start_date) return false;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    const startDate = new Date(coupon.start_date);
    startDate.setHours(0, 0, 0, 0);
    return startDate >= tomorrow;
  }, [ coupon.start_date ]);
  // クーポンが終了しているかどうかを判定（終了日が設定されていて、かつ過去）
  const isExpired = React.useMemo(() => {
    if (!coupon.end_date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = new Date(coupon.end_date);
    endDate.setHours(0, 0, 0, 0);
    return endDate < today;
  }, [ coupon.end_date ]);
  // フォームデータが変更されたかチェック
  useEffect(() => {
    if (initialFormData) {
      const isChanged =
        formData.code !== initialFormData.code ||
        formData.name !== initialFormData.name ||
        formData.discount_type !== initialFormData.discount_type ||
        formData.discount_value !== initialFormData.discount_value ||
        formData.start_date !== initialFormData.start_date ||
        formData.end_date !== initialFormData.end_date ||
        formData.usage_limit !== initialFormData.usage_limit ||
        formData.remarks !== initialFormData.remarks ||
        formData.disabled !== initialFormData.disabled;
      setHasChanges(isChanged);
    }
  }, [ formData, initialFormData ]);
  useEffect(() => {
    if (coupon && open) {
      const newFormData = {
        code: coupon.code,
        name: coupon.name || '',
        discount_type: coupon.discount_type,
        discount_value: coupon.discount_value,
        start_date: coupon.start_date,
        end_date: coupon.end_date,
        usage_limit: coupon.usage_limit,
        remarks: coupon.remarks || '',
        disabled: coupon.disabled,
      };
      setFormData(newFormData);
      setInitialFormData(newFormData);
      setFieldErrors({});
      setErrors([]);
      setApiErrors([]);
      setHasChanges(false);
      setShowImmediateStartConfirm(false);
    }
  }, [ coupon, open ]);
  const handleChange = (field: keyof CouponUpdateRequest) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    const value = event.target.value;
    if (field === 'discount_value') {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue) && numValue >= 0) {
        setFormData({ ...formData, [field]: numValue });
      }
    } else if (field === 'usage_limit') {
      const numValue = value === '' ? null : parseInt(value, 10);
      if (numValue === null || (!isNaN(numValue) && numValue >= 0)) {
        setFormData({ ...formData, [field]: numValue });
      }
    } else if (field === 'disabled') {
      setFormData({ ...formData, [field]: event.target.checked });
    } else {
      setFormData({ ...formData, [field]: value });
    }
    // フィールドエラーをクリア
    if (fieldErrors[field]) {
      setFieldErrors({ ...fieldErrors, [field]: '' });
    }
  };
  const validateForm = (): boolean => {
    const newFieldErrors: FormErrors = {};
    const newErrors: string[] = [];
    // 終了したクーポンの場合は、備考のみ変更可能なのでバリデーションをスキップ
    if (isExpired) {
      setFieldErrors(newFieldErrors);
      setErrors(newErrors);
      return true;
    }
    // クーポンコードのバリデーション（未来のクーポンの場合は必須）
    if (isFuture) {
      if (!formData.code) {
        newFieldErrors.code = 'クーポンコードは必須です';
        newErrors.push(newFieldErrors.code);
      } else if (formData.code.length < 4 || formData.code.length > 10) {
        newFieldErrors.code = 'クーポンコードは4〜10文字で入力してください';
        newErrors.push(newFieldErrors.code);
      } else if (!/^[A-Za-z0-9]+$/.test(formData.code)) {
        newFieldErrors.code = 'クーポンコードは英数字のみ使用できます';
        newErrors.push(newFieldErrors.code);
      }
    }
    // クーポン名のバリデーション
    if (!formData.name) {
      newFieldErrors.name = 'クーポン名は必須です';
      newErrors.push(newFieldErrors.name);
    } else if (formData.name.length > 20) {
      newFieldErrors.name = 'クーポン名は20文字以内で入力してください';
      newErrors.push(newFieldErrors.name);
    }
    // 割引値のバリデーション
    if (formData.discount_type === 'RATE' && (formData.discount_value < 1 || formData.discount_value > 100)) {
      newFieldErrors.discount_value = 'パーセント割引は1〜100の範囲で入力してください';
      newErrors.push(newFieldErrors.discount_value);
    } else if (formData.discount_value < 1) {
      newFieldErrors.discount_value = '割引値は1以上で入力してください';
      newErrors.push(newFieldErrors.discount_value);
    }
    // 日付のバリデーション
    if (!formData.start_date) {
      newFieldErrors.start_date = '開始日は必須です';
      newErrors.push(newFieldErrors.start_date);
    }
    // end_dateが入力されている場合のみ、開始日との比較をチェック
    if (formData.start_date && formData.end_date && formData.start_date > formData.end_date) {
      newFieldErrors.end_date = '終了日は開始日以降の日付を指定してください';
      newErrors.push(newFieldErrors.end_date);
    }
    // 利用回数のバリデーション（任意入力）
    if (formData.usage_limit !== null && formData.usage_limit !== undefined && formData.usage_limit < 1) {
      newFieldErrors.usage_limit = '利用上限は1以上で入力してください';
      newErrors.push(newFieldErrors.usage_limit);
    }
    setFieldErrors(newFieldErrors);
    setErrors(newErrors);
    return newErrors.length === 0;
  };
  const handleSubmit = async() => {
    if (!validateForm()) {
      return;
    }
    
    // 未来日のクーポンのstart_dateを今日に変更する場合は確認ダイアログを表示
    const today = formatDateToISO(new Date());
    if (isFuture && formData.start_date === today && !showImmediateStartConfirm) {
      setShowImmediateStartConfirm(true);
      return;
    }
    
    setLoading(true);
    setErrors([]);
    setApiErrors([]);
    try {
      const updateData: CouponUpdateRequest = {
        ...formData,
        end_date: formData.end_date || undefined,
        usage_limit: formData.usage_limit ?? undefined,
      };
      // codeフィールドは未来のクーポンでかつ変更された場合のみ送信
      if (!isFuture || formData.code === coupon.code) {
        delete updateData.code;
      }
      await updateCoupon(coupon.id, updateData);
      onSuccess();
      onClose();
    } catch (error: any) {
      if (error.response?.data?.errors) {
        setApiErrors(error.response.data.errors);
      } else {
        setApiErrors([ 'クーポンの更新に失敗しました' ]);
      }
    } finally {
      setLoading(false);
      setShowImmediateStartConfirm(false);
    }
  };
  
  // 確認ダイアログからの続行処理
  const handleConfirmImmediateStart = () => {
    setShowImmediateStartConfirm(false);
    handleSubmit();
  };
  
  return (
    <>
      <Dialog
      open={open}
      onClose={(_event: React.SyntheticEvent, reason?: string) => {
        // backdropClickやescapeKeyDownによる閉じるを無効化
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>クーポン編集</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {(errors.length > 0 || apiErrors.length > 0) && (
            <Grid item xs={12}>
              <Alert severity="error">
                {errors.map((error: string, index: number) => (
                  <div key={index}>{error}</div>
                ))}
                {apiErrors.map((error: string, index: number) => (
                  <div key={index + errors.length}>{error}</div>
                ))}
              </Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="クーポン名"
              value={formData.name}
              onChange={handleChange('name')}
              error={!!fieldErrors.name}
              helperText={fieldErrors.name || (isExpired ? '終了したクーポンは変更できません' : '最大20文字')}
              required
              disabled={isExpired}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="クーポンコード"
              value={formData.code}
              onChange={handleChange('code')}
              disabled={!isFuture}
              error={!!fieldErrors.code}
              helperText={
                fieldErrors.code ||
                (isFuture ? '4〜10文字の半角英数字（A-Z, a-z, 0-9）' : 'クーポンコードは変更できません')
              }
              required={isFuture}
              inputProps={{ maxLength: 10 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>割引タイプ</InputLabel>
              <Select
                value={formData.discount_type}
                onChange={handleChange('discount_type')}
                label="割引タイプ"
                disabled={isActive}
                error={!!fieldErrors.discount_type}
              >
                {Object.entries(COUPON_DISCOUNT_TYPES).map(([ key, value ]) => (
                  <MenuItem key={key} value={key}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText error={!!fieldErrors.discount_type}>
                {fieldErrors.discount_type || (isActive ? '開始済みのクーポンは変更できません' : '')}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="割引値"
              type="number"
              value={formData.discount_value}
              onChange={handleChange('discount_value')}
              error={!!fieldErrors.discount_value}
              helperText={
                fieldErrors.discount_value ||
                (isActive ? '開始済みのクーポンは変更できません' :
                  (formData.discount_type === 'FIXED' ? '１円以上' : '１〜１００%'))
              }
              required
              disabled={isActive}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {formData.discount_type === 'FIXED' ? '円' : '%'}
                  </InputAdornment>
                ),
              }}
              inputProps={{ min: 1, max: formData.discount_type === 'RATE' ? 100 : undefined }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="開始日"
              type="date"
              fullWidth
              required
              value={formData.start_date}
              onChange={handleChange('start_date')}
              error={!!fieldErrors.start_date}
              helperText={fieldErrors.start_date || (isActive ? '開始済みのクーポンは変更できません' : '')}
              InputLabelProps={{ shrink: true }}
              disabled={isActive}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box position="relative">
              <TextField
                label="終了日（任意）"
                type="date"
                fullWidth
                value={formData.end_date}
                onChange={handleChange('end_date')}
                error={!!fieldErrors.end_date}
                helperText={fieldErrors.end_date || (isExpired ? '終了したクーポンは変更できません' : '未入力の場合は無期限となります')}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: isActive ? formatDateToISO(new Date()) : formData.start_date,
                }}
                disabled={isExpired}
              />
              {!formData.end_date && (
                <Typography
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    right: '40px',  // 右側に配置（カレンダーアイコンの左）
                    transform: 'translateY(-50%)',
                    color: 'text.secondary',
                    pointerEvents: 'none',
                    fontSize: '0.875rem',
                    backgroundColor: 'background.paper',
                    padding: '0 4px',
                  }}
                >
                  期限なし
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box position="relative">
              <TextField
                fullWidth
                label="利用上限（任意）"
                type="number"
                value={formData.usage_limit ?? ''}
                onChange={handleChange('usage_limit')}
                error={!!fieldErrors.usage_limit}
                helperText={fieldErrors.usage_limit || (isExpired ? '終了したクーポンは変更できません' : '未入力の場合は無制限となります')}
                inputProps={{ min: 1 }}
                disabled={isExpired}
              />
              {(formData.usage_limit === null || formData.usage_limit === undefined) && (
                <Typography
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    right: '14px',
                    transform: 'translateY(-50%)',
                    color: 'text.secondary',
                    pointerEvents: 'none',
                    fontSize: '0.875rem',
                    backgroundColor: 'background.paper',
                    padding: '0 4px',
                  }}
                >
                  無制限
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="備考"
              value={formData.remarks}
              onChange={handleChange('remarks')}
              multiline
              rows={3}
              helperText="任意"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.disabled}
                  onChange={handleChange('disabled')}
                  disabled={isExpired}
                />
              }
              label="このクーポンを無効にする"
              disabled={isExpired}
            />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
              {isExpired ? '※ 終了したクーポンは変更できません' : '※ 更新ボタンを押すと即座に反映されます'}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          キャンセル
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !hasChanges}
        >
          更新
        </Button>
      </DialogActions>
    </Dialog>

    {/* 未来日から即日開始への変更確認ダイアログ */}
    <CouponImmediateStartConfirmDialog
      open={showImmediateStartConfirm}
      onClose={() => setShowImmediateStartConfirm(false)}
      onConfirm={handleConfirmImmediateStart}
      mode="update"
    />
    </>
  );
};
export default CouponEditDialog;
